import React from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import { SEMANTIC_SUCCESS_MID } from '../../../theme/chartColors';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './HeroBanner.css';

const slides = [
    {
        id: 1,
        tag: 'Enterprise Manufacturing Intelligence',
        title: 'Manufacturing Excellence,',
        titleHighlight: 'Strategically Delivered',
        lines: [
            'Unify production, maintenance, quality, and safety under one enterprise platform.',
            'Drive Zero Loss, Zero Defect, Zero Accident with structured digital governance.',
        ],
        icon: (
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Factory / unified platform icon */}
                <rect x="6" y="30" width="52" height="28" rx="3" fill="rgba(249,115,22,0.1)" stroke="#f97316" strokeWidth="2" />
                {/* Chimney stacks */}
                <rect x="12" y="18" width="8" height="14" rx="2" fill="rgba(249,115,22,0.2)" stroke="#f97316" strokeWidth="1.5" />
                <rect x="28" y="12" width="8" height="20" rx="2" fill="rgba(249,115,22,0.3)" stroke="#f97316" strokeWidth="1.5" />
                <rect x="44" y="22" width="8" height="10" rx="2" fill="rgba(249,115,22,0.15)" stroke="#f97316" strokeWidth="1.5" />
                {/* Windows on factory */}
                <rect x="11" y="36" width="6" height="6" rx="1" fill="#f97316" opacity="0.5" />
                <rect x="23" y="36" width="6" height="6" rx="1" fill="#f97316" opacity="0.4" />
                <rect x="35" y="36" width="6" height="6" rx="1" fill="#f97316" opacity="0.4" />
                <rect x="47" y="36" width="6" height="6" rx="1" fill="#f97316" opacity="0.5" />
                {/* Door */}
                <rect x="27" y="44" width="10" height="14" rx="1" fill="#f97316" opacity="0.3" />
                {/* Signal dots above */}
                <circle cx="16" cy="10" r="3" fill="#f97316" />
                <circle cx="32" cy="5" r="3" fill="#facc15" />
                <circle cx="48" cy="14" r="3" fill="#f97316" opacity="0.7" />
                <polyline points="16,10 32,5 48,14" stroke="#f97316" strokeWidth="1.5" strokeDasharray="3 2" fill="none" opacity="0.5" />
            </svg>
        ),
        accentColor: '#f97316',
        glowColor: '#f97316',
        stats: [
            { label: 'Modules Unified', value: '6+' },
            { label: 'Plant Coverage', value: '100%' },
            { label: 'Loss Reduction', value: 'Zero' },
        ],
        cta: 'Explore Platform',
    },
    {
        id: 2,
        tag: 'TPM & Lean Transformation',
        title: 'Digitally Enabled TPM &',
        titleHighlight: 'Lean Manufacturing',
        lines: [
            'Accelerate Total Productive Maintenance initiatives with real-time performance tracking.',
            'Eliminate waste, stabilize processes, and embed Lean discipline across operations.',
        ],
        icon: (
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Gear + trend line for TPM/Lean */}
                <circle cx="26" cy="30" r="14" stroke="#6366f1" strokeWidth="2" fill="rgba(99,102,241,0.08)" />
                <circle cx="26" cy="30" r="7" stroke="#6366f1" strokeWidth="2" fill="rgba(99,102,241,0.15)" />
                {/* Gear teeth */}
                {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => {
                    const rad = (deg * Math.PI) / 180;
                    const x1 = 26 + 14 * Math.cos(rad);
                    const y1 = 30 + 14 * Math.sin(rad);
                    const x2 = 26 + 18 * Math.cos(rad);
                    const y2 = 30 + 18 * Math.sin(rad);
                    return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#6366f1" strokeWidth="3" strokeLinecap="round" />;
                })}
                {/* Upward trend arrow */}
                <polyline points="38,50 48,38 54,42 58,28" stroke={SEMANTIC_SUCCESS_MID} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                <polygon points="58,28 52,30 56,34" fill={SEMANTIC_SUCCESS_MID} />
                <circle cx="26" cy="30" r="3" fill="#6366f1" />
            </svg>
        ),
        accentColor: '#6366f1',
        glowColor: '#6366f1',
        stats: [
            { label: 'MTBF Improved', value: '+45%' },
            { label: 'Waste Eliminated', value: '8 Types' },
            { label: 'Lean Score', value: '94%' },
        ],
        cta: 'See TPM Module',
    },
    {
        id: 3,
        tag: 'Six Sigma Driven Performance',
        title: 'Data-Driven Excellence with',
        titleHighlight: 'Six Sigma Precision',
        lines: [
            'Identify root causes using structured analytics and Why-Why methodologies.',
            'Reduce process variation, defects, and recurring losses at scale.',
        ],
        icon: (
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Bell curve / sigma */}
                <path d="M4 52 Q16 52 22 28 Q28 4 32 4 Q36 4 42 28 Q48 52 60 52" stroke="#a78bfa" strokeWidth="2.5" fill="rgba(167,139,250,0.08)" strokeLinecap="round" />
                {/* Sigma symbol */}
                <text x="26" y="40" fontSize="20" fontWeight="800" fill="#a78bfa" opacity="0.9">σ</text>
                {/* Control limit lines */}
                <line x1="4" y1="52" x2="60" y2="52" stroke="rgba(167,139,250,0.4)" strokeWidth="1.5" />
                <line x1="18" y1="10" x2="18" y2="52" stroke="rgba(167,139,250,0.25)" strokeWidth="1" strokeDasharray="3 3" />
                <line x1="46" y1="10" x2="46" y2="52" stroke="rgba(167,139,250,0.25)" strokeWidth="1" strokeDasharray="3 3" />
                {/* Data points */}
                <circle cx="12" cy="48" r="3" fill="#a78bfa" opacity="0.7" />
                <circle cx="22" cy="30" r="3" fill="#a78bfa" />
                <circle cx="32" cy="10" r="3" fill="#facc15" />
                <circle cx="42" cy="30" r="3" fill="#a78bfa" />
                <circle cx="52" cy="48" r="3" fill="#a78bfa" opacity="0.7" />
            </svg>
        ),
        accentColor: '#a78bfa',
        glowColor: '#8b5cf6',
        stats: [
            { label: 'Defect Reduction', value: '-68%' },
            { label: 'RCA Accuracy', value: '97%' },
            { label: 'Process Sigma', value: '4.8σ' },
        ],
        cta: 'Explore Six Sigma',
    },
    {
        id: 4,
        tag: 'OEE & Loss Governance',
        title: 'Operational Performance,',
        titleHighlight: 'Quantified',
        lines: [
            'Monitor Availability, Performance, and Quality with automated data capture.',
            'Convert the 16 Big Losses into measurable productivity gains.',
        ],
        icon: (
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* OEE ring */}
                <circle cx="32" cy="32" r="24" stroke="rgba(16,185,129,0.25)" strokeWidth="4" />
                <circle cx="32" cy="32" r="24" stroke={SEMANTIC_SUCCESS_MID} strokeWidth="4"
                    strokeDasharray="87.4 150.8" strokeLinecap="round"
                    style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }} />
                {/* Inner APQ rings */}
                <circle cx="32" cy="32" r="16" stroke="rgba(99,102,241,0.2)" strokeWidth="3" />
                <circle cx="32" cy="32" r="16" stroke="#60a5fa" strokeWidth="3"
                    strokeDasharray="60 100.5" strokeLinecap="round"
                    style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }} />
                {/* OEE text */}
                <text x="32" y="29" textAnchor="middle" fontSize="8" fontWeight="700" fill="#f8fafc">87.4%</text>
                <text x="32" y="38" textAnchor="middle" fontSize="5" fill="#94a3b8" letterSpacing="1">OEE</text>
                {/* Loss indicators */}
                <rect x="2" y="48" width="60" height="3" rx="1.5" fill="rgba(16,185,129,0.15)" />
                <rect x="2" y="48" width="45" height="3" rx="1.5" fill={SEMANTIC_SUCCESS_MID} opacity="0.6" />
            </svg>
        ),
        accentColor: SEMANTIC_SUCCESS_MID,
        glowColor: SEMANTIC_SUCCESS_MID,
        stats: [
            { label: 'OEE Average', value: '87.4%' },
            { label: 'Big Losses', value: '16 Types' },
            { label: 'Productivity Gain', value: '+31%' },
        ],
        cta: 'View OEE Analytics',
    },
    {
        id: 5,
        tag: 'Enterprise-Grade Safety & Compliance',
        title: 'Integrated Safety &',
        titleHighlight: 'Risk Management',
        lines: [
            'Standardize audits, incident management, RCA, and CAPA workflows.',
            'Strengthen compliance, accountability, and plant-wide safety culture.',
        ],
        icon: (
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Shield */}
                <path d="M32 4 L56 14 L56 36 C56 50 42 60 32 62 C22 60 8 50 8 36 L8 14 Z"
                    fill="rgba(251,191,36,0.08)" stroke="#fbbf24" strokeWidth="2" />
                {/* Checkmark */}
                <path d="M20 32 L28 40 L44 24" stroke="#fbbf24" strokeWidth="3"
                    strokeLinecap="round" strokeLinejoin="round" />
                {/* Compliance bars */}
                <rect x="10" y="46" width="6" height="10" rx="1" fill="#fbbf24" opacity="0.4" />
                <rect x="18" y="42" width="6" height="14" rx="1" fill="#fbbf24" opacity="0.5" />
                <rect x="26" y="38" width="6" height="18" rx="1" fill="#fbbf24" opacity="0.6" />
                <rect x="34" y="34" width="6" height="22" rx="1" fill="#fbbf24" opacity="0.7" />
                <rect x="42" y="30" width="6" height="26" rx="1" fill="#fbbf24" opacity="0.55" />
            </svg>
        ),
        accentColor: '#fbbf24',
        glowColor: '#f59e0b',
        stats: [
            { label: 'Incidents Reduced', value: '-62%' },
            { label: 'Compliance Score', value: '98%' },
            { label: 'CAPA Closure', value: '100%' },
        ],
        cta: 'See Safety Suite',
    },
];

const PrevArrow = ({ onClick }) => (
    <button className="hero-arrow hero-arrow-prev" onClick={onClick} aria-label="Previous slide">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
        </svg>
    </button>
);

const NextArrow = ({ onClick }) => (
    <button className="hero-arrow hero-arrow-next" onClick={onClick} aria-label="Next slide">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
        </svg>
    </button>
);

const HeroBanner = () => {
    const settings = {
        dots: true,
        infinite: true,
        speed: 700,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5500,
        pauseOnHover: true,
        fade: true,
        cssEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
        prevArrow: <PrevArrow />,
        nextArrow: <NextArrow />,
        dotsClass: 'hero-slick-dots',
        appendDots: (dots) => <ul className="hero-slick-dots">{dots}</ul>,
        customPaging: () => <button className="hero-dot-btn" />,
    };

    return (
        <section className="hero-slider-section">
            <div className="hero-bg-grid" />
            <Slider {...settings}>
                {slides.map((slide) => (
                    <div key={slide.id}>
                        <div
                            className="hero-slide"
                            style={{
                                '--slide-accent': slide.accentColor,
                                '--slide-glow': slide.glowColor,
                            }}
                        >
                            <div className="hero-slide-glow hero-slide-glow-1" />
                            <div className="hero-slide-glow hero-slide-glow-2" />

                            <div className="container hero-slide-inner">
                                {/* Left: Content */}
                                <div className="hero-slide-content">
                                    <div className="hero-tag">
                                        <span className="hero-tag-dot" />
                                        {slide.tag}
                                    </div>

                                    <h1 className="hero-slide-title">
                                        {slide.title}{' '}
                                        <span className="hero-slide-highlight">
                                            {slide.titleHighlight}
                                        </span>
                                    </h1>

                                    <ul className="hero-slide-desc">
                                        {slide.lines.map((line, i) => (
                                            <li key={i}>{line}</li>
                                        ))}
                                    </ul>

                                    <div className="hero-slide-actions">
                                        <Link to="/book-now" className="btn btn-primary btn-lg hero-cta-btn">
                                            {slide.cta}
                                        </Link>
                                        <Link to="/book-now" className="btn btn-outline btn-lg">
                                            Watch Demo
                                        </Link>
                                    </div>

                                    <div className="hero-slide-stats">
                                        {slide.stats.map((stat, i) => (
                                            <div key={i} className="hero-stat glass-panel">
                                                <span className="hero-stat-value">{stat.value}</span>
                                                <span className="hero-stat-label">{stat.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Right: Visual */}
                                <div className="hero-slide-visual">
                                    <div className="hero-visual-card glass-panel">
                                        <div className="hero-visual-icon">
                                            {slide.icon}
                                        </div>
                                        <div className="hero-visual-label">{slide.tag}</div>
                                    </div>
                                    <div className="hero-ring hero-ring-1" />
                                    <div className="hero-ring hero-ring-2" />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </Slider>
        </section>
    );
};

export default HeroBanner;
